import { FC } from "react";
import Head from "next/head";
import { useAppContext } from "@/src/config/AppProvider";
import { useRouter } from "next/router";

interface IPageHeadProps {
  title?: string;
  ignoreTitleSuffix?: boolean;
  description?: string;
}

const PageHead: FC<IPageHeadProps> = ({ title, ignoreTitleSuffix = false, description }) => {
  const { licenceSettings } = useAppContext();
  const router = useRouter();
  const _title = title ? (ignoreTitleSuffix ? title : `${title} - ${licenceSettings.SiteDescription}`) : licenceSettings.SiteDescription;
  const _description = description ?? licenceSettings.SiteDescription;

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const currentUrl = `${siteUrl}${router.asPath}`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: licenceSettings.SiteTitle,
    url: siteUrl,
    logo: licenceSettings.LogoUrl,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: licenceSettings.PhoneNumber,
      email: licenceSettings.ContactEmail,
      contactType: "Customer Service"
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: licenceSettings.Address
    }
  };

  return (
    <Head>
      {/* Temel Meta Etiketleri */}
      <title>{_title}</title>
      <meta name="description" content={_description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={currentUrl} />
      <link rel="icon" href={licenceSettings.FaviconUrl} />

      {/* Open Graph Etiketleri */}
      <meta property="og:title" content={_title} />
      <meta property="og:description" content={_description} />
      <meta property="og:image" content={licenceSettings.LogoUrl} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={licenceSettings.Language.LanguageName} />

      {/* Twitter Card Etiketleri */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={_title} />
      <meta name="twitter:description" content={_description} />
      <meta name="twitter:image" content={licenceSettings.LogoUrl} />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData)
        }}
      />
    </Head>
  );
};

export default PageHead;
